import React, { useState, useMemo, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { getPaymentsList } from 'services/clients/user';
import Switch from 'components/Switch';
import config from 'config';
import LoadingComponent from 'components/LoadingComponent';
import { PaymentMethod } from 'types/Billing';
import NewCreditCardForm from './NewCreditCardForm';
import SavedPaymentMethods from './SavedPaymentMethods';
import { NewBankForm } from './NewBankForm';
import { OnFormIsValid } from './interfaces';

interface Props {
  maxWidth?: number;
  onFormIsValid: OnFormIsValid;
}

type PaymentMethodId = 'newCard' | 'newBank' | 'savedMethods';

export const PaymentForm: React.FC<Props> = ({ onFormIsValid, maxWidth }) => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodId>('newCard');
  const [paymentList, setPaymentList] = useState<PaymentMethod[]>([]);
  const [defaultPaymentMethod, setDefaultPaymentMethod] = useState<PaymentMethod | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const stripePromise = useMemo(() => loadStripe(config.stripe_api_key), []);

  useEffect(() => {
    const getMethods = async () => {
      const { paymentMethods, defaultPaymentMethodId } = await getPaymentsList();
      const allPaymentMethods = [...paymentMethods.bankAccounts, ...paymentMethods.cards];
      setDefaultPaymentMethod(allPaymentMethods.find((pm) => pm.id === defaultPaymentMethodId));
      setPaymentList(allPaymentMethods);
      if (allPaymentMethods && allPaymentMethods.length > 0) {
        setPaymentMethod('savedMethods');
      }
      setIsLoading(false);
    };
    getMethods();
  }, []);

  let paymentForm = null;
  switch (paymentMethod) {
    case 'newCard':
      paymentForm = <NewCreditCardForm onFormIsValid={onFormIsValid} />;
      break;
    case 'newBank':
      paymentForm = <NewBankForm onFormIsValid={onFormIsValid} />;
      break;
    case 'savedMethods':
      paymentForm = (
        <SavedPaymentMethods
          paymentList={paymentList}
          onFormIsValid={onFormIsValid}
          defaultPaymentMethod={defaultPaymentMethod}
        />
      );
      break;
    default:
      break;
  }

  return (
    <div className="w-full" style={{ maxWidth }}>
      <div className="p-4 bg-white border border-blue-200 rounded-md">
        <div className="w-full flex flex-col space-y-4 items-start">
          <Switch
            items={[
              { value: 'newCard', label: 'New credit card' },
              { value: 'newBank', label: 'New bank account' },
              { value: 'savedMethods', label: 'Saved Payment Methods' },
            ]}
            activeItem={paymentMethod}
            onItemSelected={(item) => setPaymentMethod(item.value as PaymentMethodId)}
            minWidth={188}
          />
          <LoadingComponent isLoading={isLoading}>
            <div className="w-full">
              {/* TODO: Remove this once stripe updates their types */}
              {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
              {/* @ts-ignore */}
              <Elements stripe={stripePromise}>{paymentForm}</Elements>
            </div>
          </LoadingComponent>
        </div>
      </div>
    </div>
  );
};
PaymentForm.displayName = 'Checkout.PaymentForm';
